import React from 'react';
import {
  Button,
  ComboBox,
  Group,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Text,
} from 'react-aria-components';

import { iconChevronDown } from '../../icons/glyphs/chevron-down';
import { iconClose } from '../../icons/glyphs/close';
import { Icon } from '../../icons/icon';

import type { ComboboxOption, ComboboxProps } from './combobox';
import { ComboboxSpinner } from './combobox-spinner';

import styles from './combobox.module.css';
import popoverStyles from './combobox-popover.module.css';

/**
 * The inline presentation: react-aria's ComboBox with a popover under the field.
 */
export function ComboboxPopover(
  props: ComboboxProps & { onOpenChange: (isOpen: boolean) => void },
): React.ReactElement {
  const {
    allowsCustomValue = false,
    allowsEmptyCollection = true,
    description,
    emptyText = 'No results found.',
    hint,
    inputValue,
    isDisabled = false,
    isInvalid = false,
    isLoading = false,
    isRequired = false,
    label,
    onInputChange,
    onKeyDown,
    onOpenChange,
    onSelectionChange,
    openOnFocus = true,
    options,
    placeholder,
    selectedKey,
  } = props;
  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <ComboBox
      allowsCustomValue={allowsCustomValue}
      allowsEmptyCollection={allowsEmptyCollection}
      aria-label={
        label === undefined ? placeholder ?? 'Select an option' : undefined
      }
      className={styles['ns-combobox']}
      inputValue={inputValue}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      // Supplying items disables react-aria's built-in filtering. Callers may
      // therefore provide either locally or remotely filtered options.
      items={options}
      menuTrigger={openOnFocus ? 'focus' : 'input'}
      onChange={key => onSelectionChange(typeof key === 'string' ? key : null)}
      onInputChange={onInputChange}
      onKeyDown={onKeyDown}
      onOpenChange={onOpenChange}
      value={selectedKey}
    >
      {label !== undefined && (
        <Label className={styles['ns-combobox__label']}>
          {label}
          {isRequired && (
            <span
              aria-hidden
              className={styles['ns-combobox__secondary-label']}
            >
              *
            </span>
          )}
        </Label>
      )}
      <Group className={styles['ns-combobox__input']}>
        <Input
          ref={inputRef}
          className={styles['ns-combobox__input-field']}
          placeholder={placeholder}
        />
        {selectedKey !== null && (
          <Button
            aria-label="Clear selection"
            className={styles['ns-combobox__input-clear']}
            isDisabled={isDisabled}
            onPress={() => {
              onSelectionChange(null);
              // Without this, focus falls to the body once the button unmounts.
              inputRef.current?.focus();
            }}
            slot={null}
          >
            <Icon glyph={iconClose} size={16} />
          </Button>
        )}
        {isLoading && (
          <ComboboxSpinner className={styles['ns-combobox__spinner']} />
        )}
        <Button className={styles['ns-combobox__input-chevron']}>
          <Icon glyph={iconChevronDown} size={16} />
        </Button>
      </Group>

      {description !== undefined && (
        <Text className={styles['ns-combobox__description']} slot="description">
          {description}
        </Text>
      )}

      <Popover className={popoverStyles['ns-combobox__popover']}>
        <ListBox
          className={styles['ns-combobox__list']}
          renderEmptyState={() => (
            <div className={styles['ns-combobox__no-results']}>{emptyText}</div>
          )}
        >
          {(item: ComboboxOption) => (
            <ListBoxItem
              className={styles['ns-combobox__item']}
              id={item.value}
              isDisabled={item.disabled}
              textValue={item.label}
            >
              <span className={styles['ns-combobox__item-label']}>
                {item.label}
              </span>
            </ListBoxItem>
          )}
        </ListBox>

        {hint !== undefined && (
          <div className={styles['ns-combobox__hint']} role="note">
            {hint}
          </div>
        )}
      </Popover>
    </ComboBox>
  );
}
